import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FormProvider, useForm, DeepPartial } from "react-hook-form";
import { object, string, boolean, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    addToast,
    Button,
    Modal,
    ModalBody,
    ModalContent,
    useDisclosure,
} from "@heroui/react";
import TextInput from "@/components/inputs/TextInput";
import SelectInput from "@/components/inputs/SelectInput";
import corn from "@/assets/images/logo-corn@2x.png";
import ntimeLogo from "@/assets/images/H-NTime-R.png";
import useRequest from "@/lib/request/useRequest";
import { useMemo, useEffect } from "react";
import { sortArrayByKey } from "@/lib/utils/sorting";
import CheckboxInput from "@/components/inputs/CheckboxInput";

type ServiceProvider = {
    id: number;
    name: string;
    adminEmail: string;
    image: string;
};

const TEMP_PASSWORD = "ROLLCLONES";

const PROVIDERS: ServiceProvider[] = [
    {
        id: 1,
        name: "Ag Partners Cooperative",
        adminEmail: "enoll@agpatnerscoop.com",
        image: "/providerLogos/agPartners.png",
    },
    {
        id: 2,
        name: "Midplains Ag",
        adminEmail: "tiffany@midplns.com",
        image: "/providerLogos/midplainsAg.png",
    },
    {
        id: 3,
        name: "Precision Agronomy",
        adminEmail: "nick@precisionagronomy.com",
        image: "/providerLogos/precisionAgronomy.png",
    },
    {
        id: 4,
        name: "DDM Agronomics",
        adminEmail: "ddmagronomics@gmail.com",
        image: "",
    },
    {
        id: 5,
        name: "CVA",
        adminEmail: "jason.kock@cvacoop.com",
        image: "/providerLogos/cva.png",
    },
    {
        id: 6,
        name: "FarmReign",
        adminEmail: "luke.cure@farmreign.ag",
        image: "/providerLogos/farmReign.png",
    },
    {
        id: 7,
        name: "MKC",
        adminEmail: "rbenisch@mkcoop.com",
        image: "/providerLogos/mkc.jpg",
    },
];

export const Route = createFileRoute("/(auth)/register")({
    component: RouteComponent,
});

const RegisterSchema = object({
    first_name: string().required("First name is required"),
    last_name: string().required("Last name is required"),
    email: string().email("Enter a valid email").required("Email is required"),
    organization: string().required("Organization is required"),
    account_type: string()
        .oneOf(["grower", "consultant"], "Choose grower or consultant")
        .required("Account type is required"),
    provider: string().when("account_type", {
        is: (t: string) => t === "grower",
        then: (s) => s.required("Provider is required for growers"),
        otherwise: (s) => s.optional(),
    }),
    agreed_to_terms: boolean().oneOf([true], "You must accept the Terms"),
    username: string().default(""),
    phone_number: string().default(""),
    address_1: string().default(""),
    address_2: string().default(""),
    city: string().default(""),
    state: string().default(""),
    zip: string().default(""),

    countryCode: string().default("+1"),
    phone_display: string().default(""),
});
type RegisterForm = InferType<typeof RegisterSchema>;

function RouteComponent() {
    const navigate = useNavigate();
    const { axiosRequest } = useRequest();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const providerOptions = useMemo(
        () =>
            sortArrayByKey(PROVIDERS, "name").map((p) => ({
                id: p.name,
                description: p.name,
            })),
        [],
    );

    const defaultValues: DeepPartial<RegisterForm> = {
        countryCode: "+1",
    };

    const f = useForm<RegisterForm>({
        resolver: yupResolver(RegisterSchema),
        mode: "onBlur",
        defaultValues,
    });

    const accountType = f.watch("account_type");

    useEffect(() => {
        if (accountType !== "grower") {
            f.setValue("provider", "", {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    }, [accountType, f]);

    function formatDisplayPhone(raw: string) {
        const d = raw.replace(/\D/g, "").slice(-10);
        if (!d) return "";
        const p1 = d.slice(0, 3),
            p2 = d.slice(3, 6),
            p3 = d.slice(6);
        return p3 ? `(${p1}) ${p2}-${p3}` : p2 ? `(${p1}) ${p2}` : `(${p1}`;
    }

    const onSubmit = async (values: RegisterForm) => {
        const emailLower = values.email.toLowerCase();
        const username = emailLower;
        const digits = (values.phone_display || "")
            .replace(/\D/g, "")
            .slice(-10);
        const cc = values.countryCode?.startsWith("+")
            ? values.countryCode
            : `+${values.countryCode || "1"}`;
        const phone_number = `${cc}${digits}`;

        const payload = {
            first_name: values.first_name,
            last_name: values.last_name,
            email: emailLower,
            username,
            password: TEMP_PASSWORD,
            phone_number,
            address_1: values.address_1,
            address_2: values.address_2,
            city: values.city,
            state: values.state,
            zip: values.zip,
            organization: values.organization,
            account_type: values.account_type,
            provider: values.provider,
            agreed_to_terms: values.agreed_to_terms,
        };

        try {
            await axiosRequest({
                url: "users/",
                method: "post",
                data: payload,
            });
            onOpen();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            const data = err?.response?.data ?? err?.data ?? {};
            const fieldErrors = data.errors || data.fieldErrors;
            if (fieldErrors && typeof fieldErrors === "object") {
                Object.entries(fieldErrors).forEach(([k, msg]) => {
                    if (k in values) {
                        f.setError(k as keyof RegisterForm, {
                            type: "server",
                            message: String(msg),
                        });
                    }
                });
            } else {
                addToast({
                    description:
                        "Registration failed. Please check your details and try again.",
                    color: "danger",
                });
            }
        }
    };

    const blurb = (
        <div className="p-4 flex flex-col gap-4 pt-12 w-full">
            <div className="text-green-400 font-bold text-lg">
                Sentinel® Insights
            </div>
            <div className="text-3xl">
                Profitable and confident Nitrogen management
            </div>
            <div>
                Sentinel optimizes your nitrogen applications to achieve the
                most profitable outcome on your farm. It does this by measuring
                crop N demand and suggesting when, where, and how much N
                fertilizer to apply. Sentinel has been used to achieve NCGA Low
                N contest championship wins and has delivered $24/ac improvement
                in profitability in benchmarking trials. Are you ready to use
                Sentinel to revolutionize your Nitrogen management?
            </div>
        </div>
    );

    return (
        <div className="flex flex-col items-center md:grid md:grid-cols-[1fr_2fr] h-screen w-screen justify-items-center md:p-0">
            <div className="bg-brandTeal-500 w-full h-full hidden md:block">
                <div className="relative h-full w-full text-white">
                    {blurb}
                    <img
                        src={corn}
                        className="absolute translate-x-[51.25%] w-[20rem] bottom-0 right-0"
                    />
                </div>
            </div>

            <div className="pt-8 px-2 w-full md:h-screen md:overflow-y-auto md:justify-self-center">
                <FormProvider {...f}>
                    <form onSubmit={f.handleSubmit(onSubmit)} noValidate>
                        <div className="flex flex-col w-[90%] mx-auto items-stretch md:max-w-[36rem] gap-4">
                            <img
                                src={ntimeLogo}
                                alt="N‑Time"
                                className="block w-full max-w-[18rem] md:max-w-[25rem] mx-auto px-6 md:px-8 h-auto"
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <TextInput
                                    name="first_name"
                                    label="First Name"
                                    size="sm"
                                />
                                <TextInput
                                    name="last_name"
                                    label="Last Name"
                                    size="sm"
                                />
                            </div>

                            <TextInput
                                name="email"
                                label="Email"
                                type="email"
                                size="sm"
                            />

                            <TextInput
                                name="organization"
                                label="Organization"
                                size="sm"
                            />

                            <SelectInput
                                name="account_type"
                                label="Account Type"
                                size="sm"
                                placeholder="Select..."
                                options={[
                                    { id: "grower", description: "Grower" },
                                    {
                                        id: "consultant",
                                        description: "Consultant",
                                    },
                                ]}
                            />

                            {accountType === "grower" && (
                                <SelectInput
                                    name="provider"
                                    label="Provider"
                                    size="sm"
                                    placeholder="Select..."
                                    options={providerOptions}
                                />
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-[110px_1fr] gap-4">
                                <TextInput
                                    name="countryCode"
                                    label="Country Code"
                                    type="tel"
                                    size="sm"
                                    inputMode="tel"
                                    maxLength={5}
                                    placeholder="+1"
                                />

                                <TextInput
                                    name="phone_display"
                                    label="Phone"
                                    type="tel"
                                    size="sm"
                                    inputMode="tel"
                                    maxLength={14}
                                    onChange={() => {
                                        const v =
                                            f.getValues("phone_display") ?? "";
                                        f.setValue(
                                            "phone_display",
                                            formatDisplayPhone(v),
                                            {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                            },
                                        );
                                    }}
                                />
                            </div>

                            <TextInput
                                name="address_1"
                                label="Address line 1"
                                size="sm"
                            />
                            <TextInput
                                name="address_2"
                                label="Address line 2"
                                size="sm"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <TextInput name="city" label="City" size="sm" />
                                <TextInput
                                    name="state"
                                    label="State"
                                    size="sm"
                                />
                                <TextInput name="zip" label="ZIP" size="sm" />
                            </div>

                            <div className="flex items-center gap-3">
                                <CheckboxInput
                                    id="agreed_to_terms"
                                    {...f.register("agreed_to_terms")}
                                    className="h-4 w-4"
                                    size="sm"
                                />
                                <label
                                    htmlFor="agreed_to_terms"
                                    className="text-sm ml-2"
                                >
                                    I agree to the{" "}
                                    <a
                                        href="/assets/documents/termsOfService.pdf"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline cursor-pointer text-gray-500 hover:text-[#008ea9]"
                                    >
                                        Terms of Service,
                                    </a>{" "}
                                    <a
                                        href="/assets/documents/dataUseAgreement.pdf"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline cursor-pointer text-gray-500 hover:text-[#008ea9]"
                                    >
                                        Data Use Agreement,
                                    </a>{" "}
                                    <a
                                        href="/assets/documents/privacyPolicy.pdf"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline cursor-pointer text-gray-500 hover:text-[#008ea9]"
                                    >
                                        Privacy Policy,
                                    </a>{" "}
                                    and to receive text messages from Sentinel,
                                    message and data rates may apply, reply STOP
                                    to opt out
                                </label>
                            </div>
                            {f.formState.errors.agreed_to_terms && (
                                <p className="mt-[-0.25rem] text-xs text-red-600">
                                    {
                                        f.formState.errors.agreed_to_terms
                                            .message as string
                                    }
                                </p>
                            )}

                            <Button
                                type="submit"
                                color="primary"
                                size="sm"
                                variant="solid"
                                isLoading={f.formState.isSubmitting}
                                isDisabled={f.formState.isSubmitting}
                            >
                                Create Account
                            </Button>

                            <div className="text-sm mb-2">
                                Already have an account?{" "}
                                <Link
                                    to="/auth"
                                    className="text-brandGreen-500 underline"
                                >
                                    Log in
                                </Link>
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </div>
            <div className="md:hidden bg-brandNavy-500 mt-12 text-white flex-1 flex flex-col">
                {blurb}
            </div>

            <Modal
                isOpen={isOpen}
                onOpenChange={(open) => {
                    if (open) onOpen();
                    else onClose();
                }}
            >
                <ModalContent>
                    <ModalBody className="py-6">
                        <p className="mb-4 text-base font-medium">
                            Thanks for registering!
                        </p>
                        <Button
                            onPress={() => {
                                onClose();
                                navigate({ to: "/auth", replace: true });
                            }}
                        >
                            Go to login
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </div>
    );
}
